import type { AxiosError } from 'axios';
import { Box, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  getListOrganisationConnectionInvitesQueryKey,
  getListOrganisationConnectionsQueryKey,
  useInviteOrganisationConnection,
  useListOrganisationConnectionInvites,
  useListOrganisationConnections
} from '@/api/generated/organisation-connections/organisation-connections';
import { IconFigmaMoreHorizontal } from '@/assets/icons';
import {
  CardSection,
  InfoBanner,
  ListPagination,
  MutationButton,
  PageHeading,
  tableRowStyle,
  TextField
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { authErrorMessage } from '@/features/auth/utils/authErrorMessage';
import { getInviteStatus, InviteStatusBadge } from '@/features/users/components/InviteStatusBadge';
import { formatDate, formatDateTimeParts } from '@/utils/format';
import { useLocalPagination } from '@/utils/useLocalPagination';

// Isti obrazac kao RegisterForm/ConnectForm — backend već vraća specifičan,
// lokalizirani razlog (organizacije već povezane, već postoji veza...).
const inviteErrorMessage = (
  error: unknown,
  isPaused: boolean,
  t: (key: string) => string,
  fallback: string
): string =>
  authErrorMessage(error, isPaused, t, () => {
    const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
    return typeof backendMessage === 'string' ? backendMessage : fallback;
  });

const schema = z.object({
  email: z.string().email()
});

type FormValues = z.infer<typeof schema>;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

const cellStyle = { color: 'var(--color-text-secondary)' };

export const PartnerLinkingPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  // staleTime: 0 — druga strana (ili druga sesija) može promijeniti ovo stanje
  // (prihvatiti/poslati pozivnicu) bez ikakvog signala natrag ovoj stranici;
  // globalni 5-minutni staleTime bi inače prikazivao zastarjelo stanje sve dok
  // korisnik ne pričeka ili ne pokrene tvrdi reload.
  const { data: connectionsData, isLoading: connectionsLoading } = useListOrganisationConnections(
    undefined,
    { query: { staleTime: 0 } }
  );
  const connections = connectionsData ?? [];

  const { data: invitesData, isLoading: invitesLoading } = useListOrganisationConnectionInvites(
    undefined,
    { query: { staleTime: 0 } }
  );
  const invites = invitesData ?? [];

  const otherOrgName = (orgAName?: string, orgBName?: string) =>
    orgAName === user?.orgName ? (orgBName ?? '—') : (orgAName ?? '—');

  const connected = useMemo(
    () =>
      connections
        .filter((c) => !!c.acceptedAt)
        .sort(
          (a, b) => new Date(b.acceptedAt ?? 0).getTime() - new Date(a.acceptedAt ?? 0).getTime()
        ),
    [connections]
  );

  // Jedna tablica za SVE pozivnice — na čekanju, istekle I prihvaćene — status
  // svake se računa iz vlastitih expiresAt/usedAt (organisation_connection_invites),
  // ne iz organisation_connections (koja uopće ne postoji dok se pozivnica ne prihvati).
  const sentInvites = useMemo(
    () =>
      invites
        .slice()
        .sort(
          (a, b) => new Date(b.invitedAt ?? 0).getTime() - new Date(a.invitedAt ?? 0).getTime()
        ),
    [invites]
  );

  const { page, setPage, pageSize, setPageSize, totalPages, pageItems, offset } =
    useLocalPagination(sentInvites);

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { email: '' }
  });

  const inviteMutation = useInviteOrganisationConnection({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        form.reset();
        setPage(1);
        queryClient.invalidateQueries({ queryKey: getListOrganisationConnectionsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListOrganisationConnectionInvitesQueryKey() });
      }
    }
  });

  const handleSubmit = (values: FormValues) => {
    inviteMutation.mutate({ data: { invitedEmail: values.email } });
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <PageHeading
          title={t('users.partnerLink.title')}
          subtitle={t('users.partnerLink.subtitle')}
        />

        <CardSection title={t('users.partnerLink.connectedTitle')}>
          {!connectionsLoading && connected.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('users.partnerLink.emptyConnected')}
              </Text>
            </Center>
          )}

          {connected.map((c, index) => {
            const name = otherOrgName(c.orgAName, c.orgBName);
            return (
              <Group
                key={c.id}
                gap={20}
                px={20}
                py={13}
                wrap="nowrap"
                style={{
                  borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                }}
              >
                <Group gap={10} wrap="nowrap">
                  <Center
                    w={28}
                    h={28}
                    style={{
                      borderRadius: 6,
                      backgroundColor: 'var(--color-border-soft)',
                      border: '1px solid var(--color-border-default)',
                      flexShrink: 0
                    }}
                  >
                    <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                      {initials(name)}
                    </Text>
                  </Center>
                  <Stack gap={0}>
                    <Text fz={13} style={{ color: 'var(--color-text-primary)' }}>
                      {name}
                    </Text>
                    <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                      {t('users.partnerLink.connectedSince', {
                        date: c.acceptedAt ? formatDate(c.acceptedAt) : ''
                      })}
                    </Text>
                  </Stack>
                </Group>
              </Group>
            );
          })}
        </CardSection>

        <CardSection title={t('users.partnerLink.newInviteTitle')}>
          <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
            <Group align="flex-end" wrap="wrap" gap={12} p={18} pb={34}>
              <Box w={{ base: '100%', sm: 320 }} style={{ flex: '1 1 320px' }}>
                <TextField
                  label={t('users.partnerLink.emailLabel')}
                  placeholder={t('users.partnerLink.emailPlaceholder')}
                  {...form.getInputProps('email')}
                />
              </Box>
              <MutationButton
                type="submit"
                w={{ base: '100%', sm: 170 }}
                isPending={inviteMutation.isPending}
                isPaused={inviteMutation.isPaused}
              >
                {t('users.partnerLink.send')}
              </MutationButton>
            </Group>
            {(inviteMutation.isError || inviteMutation.isPaused) && (
              <Text px={18} pb={14} fz={13} style={{ color: 'var(--color-status-danger)' }}>
                {inviteErrorMessage(
                  inviteMutation.error,
                  inviteMutation.isPaused,
                  t,
                  t('users.partnerLink.sendError')
                )}
              </Text>
            )}
          </form>
        </CardSection>

        <CardSection title={t('users.partnerLink.sentTitle')}>
          {!invitesLoading && sentInvites.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('users.partnerLink.emptyPending')}
              </Text>
            </Center>
          )}

          {sentInvites.length > 0 && (
            <>
              <Box visibleFrom="sm">
                <div
                  className="table-header-row"
                  style={{
                    ...tableRowStyle,
                    padding: '12px 20px',
                    backgroundColor: 'var(--color-bg-canvas)',
                    borderBottom: '1px solid var(--color-border-soft)'
                  }}
                >
                  {[
                    ['users.partnerLink.colOrg', 220],
                    ['users.partnerLink.colSent', 130],
                    ['users.partnerLink.colExpires', 130],
                    ['users.partnerLink.colStatus', 130]
                  ].map(([key, width]) => (
                    <Text
                      key={key as string}
                      fz={10}
                      fw={600}
                      w={width}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t(key as string)}
                    </Text>
                  ))}
                  <Text
                    fz={10}
                    fw={600}
                    w={90}
                    ta="right"
                    style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                  >
                    {t('users.partnerLink.colActions')}
                  </Text>
                </div>

                {pageItems.map((invite, index) => {
                  const invitedAt = invite.invitedAt ?? '';
                  const expiresAt = invite.expiresAt ?? '';
                  const status = getInviteStatus(expiresAt, invite.usedAt ?? null);
                  const sent = invitedAt ? formatDateTimeParts(invitedAt) : undefined;
                  const expires = expiresAt ? formatDateTimeParts(expiresAt) : undefined;

                  return (
                    <div
                      key={invite.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Group gap={10} wrap="nowrap" w={220}>
                        <Center
                          w={28}
                          h={28}
                          style={{
                            borderRadius: 999,
                            backgroundColor: 'var(--color-bg-surface-2)',
                            border: '1px solid var(--color-border-default)',
                            flexShrink: 0
                          }}
                        >
                          <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                            {initials(invite.invitedEmail ?? '—')}
                          </Text>
                        </Center>
                        <Text fz={12.5} style={{ color: 'var(--color-text-primary)' }}>
                          {invite.invitedEmail ?? '—'}
                        </Text>
                      </Group>

                      <Stack gap={0} w={130}>
                        <Text fz={10.5} style={cellStyle}>
                          {sent?.date ?? '—'}
                        </Text>
                        <Text fz={9.5} style={{ color: 'var(--color-text-muted)' }}>
                          {sent?.time ?? ''}
                        </Text>
                      </Stack>

                      <Stack gap={0} w={130}>
                        <Text fz={10.5} style={cellStyle}>
                          {expires?.date ?? '—'}
                        </Text>
                        <Text fz={9.5} style={{ color: 'var(--color-text-muted)' }}>
                          {expires?.time ?? ''}
                        </Text>
                      </Stack>

                      <div style={{ width: 130 }}>
                        <InviteStatusBadge status={status} />
                      </div>

                      <Group w={90} justify="flex-end">
                        <Center
                          w={32}
                          h={32}
                          style={{ borderRadius: 6, backgroundColor: 'var(--color-bg-canvas)' }}
                        >
                          <IconFigmaMoreHorizontal size={16} />
                        </Center>
                      </Group>
                    </div>
                  );
                })}
              </Box>

              <Stack hiddenFrom="sm" gap={12} p={16}>
                {pageItems.map((invite) => {
                  const invitedAt = invite.invitedAt ?? '';
                  const expiresAt = invite.expiresAt ?? '';
                  const status = getInviteStatus(expiresAt, invite.usedAt ?? null);
                  const sent = invitedAt ? formatDateTimeParts(invitedAt) : undefined;
                  const expires = expiresAt ? formatDateTimeParts(expiresAt) : undefined;

                  return (
                    <Stack
                      key={invite.id}
                      gap={12}
                      p={16}
                      style={{
                        borderRadius: 12,
                        border: '1px solid var(--color-border-soft)',
                        backgroundColor: 'var(--color-bg-surface)'
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap={8} wrap="nowrap">
                          <Center
                            w={28}
                            h={28}
                            style={{
                              borderRadius: 999,
                              backgroundColor: 'var(--color-bg-surface-2)',
                              border: '1px solid var(--color-border-default)',
                              flexShrink: 0
                            }}
                          >
                            <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                              {initials(invite.invitedEmail ?? '—')}
                            </Text>
                          </Center>
                          <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                            {invite.invitedEmail ?? '—'}
                          </Text>
                        </Group>
                        <Center
                          w={32}
                          h={32}
                          style={{
                            borderRadius: 6,
                            backgroundColor: 'var(--color-bg-canvas)',
                            flexShrink: 0
                          }}
                        >
                          <IconFigmaMoreHorizontal size={16} />
                        </Center>
                      </Group>

                      <Box style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />

                      <Stack gap={8}>
                        <Group justify="space-between">
                          <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                            {t('users.partnerLink.colSent')}:
                          </Text>
                          <Text fz={11} style={cellStyle}>
                            {sent ? `${sent.date} ${sent.time}` : '—'}
                          </Text>
                        </Group>
                        <Group justify="space-between">
                          <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                            {t('users.partnerLink.colExpires')}:
                          </Text>
                          <Text fz={11} style={cellStyle}>
                            {expires ? `${expires.date} ${expires.time}` : '—'}
                          </Text>
                        </Group>
                        <Group>
                          <InviteStatusBadge status={status} />
                        </Group>
                      </Stack>
                    </Stack>
                  );
                })}
                <ListPagination
                  label={t('users.partnerLink.showing', {
                    from: sentInvites.length === 0 ? 0 : offset + 1,
                    to: offset + pageItems.length,
                    total: sentInvites.length
                  })}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  bordered={false}
                />
              </Stack>

              <Box visibleFrom="sm">
                <ListPagination
                  label={t('users.partnerLink.showing', {
                    from: sentInvites.length === 0 ? 0 : offset + 1,
                    to: offset + pageItems.length,
                    total: sentInvites.length
                  })}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </Box>
            </>
          )}
        </CardSection>

        <InfoBanner message={t('users.partnerLink.infoBanner')} />
      </Stack>
    </Container>
  );
};
